import type { NumberRangeBound, NumberRangeValue } from './NumberRangeFilter.types';

/**
 * Convert a staged bound to a finite number, or `undefined` if it represents
 * "no bound" (empty string) or an in-progress partial value (e.g. `"-"`, `"3."`).
 */
function toFiniteNumber(bound: NumberRangeBound): number | undefined {
  if (bound === '') return undefined;
  const num = typeof bound === 'number' ? bound : Number(bound);
  return Number.isFinite(num) ? num : undefined;
}

/**
 * Whether a numeric value falls within a staged range, treating an empty
 * (or in-progress) bound as "unbounded" on that side.
 *
 * Use this to test field values against a `NumberRangeFilter`'s staged value,
 * combining the result with other active filters using AND — the same way
 * every other filter field is combined.
 *
 * @example
 * ```ts
 * const matches = items.filter((item) => isValueInRange(item.dbh, dbhRange));
 * ```
 */
export function isValueInRange(value: number, range: NumberRangeValue): boolean {
  const min = toFiniteNumber(range.min);
  const max = toFiniteNumber(range.max);
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
}

/**
 * Whether a range counts as "active" — i.e. either bound has been set.
 *
 * Fold this into a filter panel's "any filters active" indicator and
 * "clear disabled" logic without writing field-specific conditionals.
 */
export function isRangeActive(range: NumberRangeValue): boolean {
  return range.min !== '' || range.max !== '';
}

/**
 * Whether a range is valid — i.e. min is not greater than max. A range with
 * an empty or in-progress bound on either side is always considered valid,
 * since there's nothing yet to compare.
 */
export function isRangeValid(range: NumberRangeValue): boolean {
  const min = toFiniteNumber(range.min);
  const max = toFiniteNumber(range.max);
  if (min === undefined || max === undefined) return true;
  return min <= max;
}

/** Return a copy of the range with min and max swapped. */
export function swapRange(range: NumberRangeValue): NumberRangeValue {
  return { min: range.max, max: range.min };
}

/** The "cleared" (fully unbounded) value for a range field. */
export const EMPTY_RANGE: NumberRangeValue = { min: '', max: '' };
